import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import moment from 'moment-timezone';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import DeleteButton from '../../components/DeleteButton';
import AnswerEditor, { draftIsValid, draftToPayload } from './AnswerEditor';
import { CHANNEL_COPY, CHANNEL_LABELS, otherChannel } from './channelCopy';
import ChannelChips from './ChannelChips';
import { scopeChips, useTagScope } from './tagScope';

const draftFromAnswer = (a) => ({
  answer: a.answer,
  all_tours: a.all_tours,
  tours: a.tours || [],
  collections: a.collections || [],
  access_tags: a.access_tags || [],
  excluded_tours: a.excluded_tours || [],
  channels: a.channels || [],
  valid_until: a.valid_until || null,
  valid_until_reason: a.valid_until_reason || null,
});

// Share vs copy, from the operator's side, lives in ChannelChips ("Used in: ...").

// "Add a trained answer": the same editor as training from a card, starting blank. Used in
// this inbox's channel (locked on), optionally shared with the other.
function NewAnswerCard({ channel, canShare, productOptions, api, onCreate, onCancel }) {
  const [question, setQuestion] = useState('');
  const [draft, setDraft] = useState({
    answer: '',
    all_tours: true,
    tours: [],
    collections: [],
    access_tags: [],
    excluded_tours: [],
    channels: [channel],
    valid_until: null,
    valid_until_reason: null,
  });
  const [saving, setSaving] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const save = async () => {
    setSaving(true);
    try {
      if (await onCreate(draftToPayload(question, draft)) !== false && mounted.current) onCancel();
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 1.5, borderRadius: '12px', borderColor: 'primary.main' }}>
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Add a trained answer</Typography>
          <TextField
            label="Question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            fullWidth
            autoFocus
            helperText={channel === 'widget'
              ? 'Write it the way a website visitor would ask it.'
              : 'Write it the way a guest would ask it.'}
          />
          <AnswerEditor
            question={question}
            draft={draft}
            onChange={setDraft}
            productOptions={productOptions}
            channel={channel}
            canShare={canShare}
            api={api}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={save} disabled={saving || !question.trim() || !draftIsValid(draft)}>
              Train with this answer
            </Button>
            <Button color="inherit" onClick={onCancel} disabled={saving}>Cancel</Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

function AnswerCard({ answer, channel, canWrite, canShare, showChannels, handlers, productOptions, api }) {
  const [editing, setEditing] = useState(false);
  const [question, setQuestion] = useState(answer.question);
  const [draft, setDraft] = useState(() => draftFromAnswer(answer));
  const [saving, setSaving] = useState(false);
  const ctx = useTagScope();
  const other = otherChannel(channel);
  const shared = answer.channels.includes(other);

  const reset = () => { setQuestion(answer.question); setDraft(draftFromAnswer(answer)); };
  // "Make a separate copy" mid-edit un-shares the answer on the server; follow it here.
  const channelsKey = answer.channels.join(',');
  useEffect(() => { setDraft((d) => ({ ...d, channels: answer.channels })); }, [channelsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // What saving will do to where the answer is used, in plain words (null = no change).
  const channelNote = (() => {
    const was = answer.channels;
    const now = draft.channels;
    if (was.length === now.length && was.every((c) => now.includes(c))) return null;
    if (!now.includes(channel)) return `Saving moves this answer to the ${CHANNEL_LABELS[other]}. It will leave this list.`;
    if (now.includes(other)) return `Saving shares this answer with the ${CHANNEL_LABELS[other]}. Edits will apply to both.`;
    return `Saving stops the ${CHANNEL_LABELS[other]} from using this answer.`;
  })();

  // A moved answer leaves this list (and unmounts) before its save settles.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const save = async () => {
    setSaving(true);
    try {
      const moved = !draft.channels.includes(channel);
      await handlers.onSave(
        answer.id,
        draftToPayload(question, draft),
        moved ? `Answer moved to the ${CHANNEL_LABELS[other]}.` : undefined,
      );
      if (mounted.current) setEditing(false);
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  if (editing) {
    return (
      <Card variant="outlined" sx={{ mb: 1.5, borderRadius: '12px' }}>
        <CardContent>
          <Stack spacing={2}>
            {shared && (
              <Alert
                severity="info"
                icon={<LinkIcon />}
                action={canShare && (
                  <Button color="inherit" size="small" onClick={() => handlers.onSplit(answer)}>
                    Make a separate copy
                  </Button>
                )}
              >
                This answer is shared with the {CHANNEL_LABELS[other]}. Changes apply there too.
              </Alert>
            )}
            <TextField label="Question" value={question} onChange={(e) => setQuestion(e.target.value)} fullWidth />
            <AnswerEditor
              question={question}
              draft={draft}
              onChange={setDraft}
              productOptions={productOptions}
              suggestedTours={answer.tours}
              channel={channel}
              canShare={canShare}
              lockChannel={false}
              api={api}
            />
            {channelNote && <Alert severity={draft.channels.includes(channel) ? 'info' : 'warning'}>{channelNote}</Alert>}
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={save} disabled={saving || !question.trim() || !draftIsValid(draft)}>Save</Button>
              <Button color="inherit" onClick={() => { setEditing(false); reset(); }}>Cancel</Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const expired = answer.valid_until && moment(answer.valid_until).isBefore(moment(), 'day');
  return (
    <Card variant="outlined" sx={{ mb: 1.5, borderRadius: '12px' }}>
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{answer.question}</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{answer.answer}</Typography>
          </Box>
          {canWrite && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0, alignSelf: 'flex-start' }}>
              <Button size="small" onClick={() => { reset(); setEditing(true); }}>Edit</Button>
              {/* CP-5545: DELETE when it deletes; a shared answer only leaves this channel, so it stays "Remove". */}
              {shared ? (
                <Button size="small" color="error" onClick={() => handlers.onRemove(answer)}>Remove</Button>
              ) : (
                <DeleteButton size="small" onClick={() => handlers.onRemove(answer)}>Delete</DeleteButton>
              )}
            </Stack>
          )}
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          {scopeChips(answer, ctx).map((c) => <Chip key={c.key} size="small" label={c.label} title={c.title} />)}
          {answer.valid_until && (
            <Chip
              size="small"
              variant="outlined"
              color={expired ? 'warning' : 'default'}
              label={`${expired ? 'Stopped' : 'Until'} ${moment(answer.valid_until).format('MMM D, YYYY')}`}
            />
          )}
        </Stack>
        {showChannels && (
          <Box sx={{ mt: 1.25 }}>
            <ChannelChips
              channel={channel}
              channels={answer.channels}
              canShare={canWrite && canShare}
              onShare={() => handlers.onShare(answer)}
              onUnshare={() => handlers.onUnshare(answer)}
              onCopy={() => handlers.onCopy(answer)}
            />
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          Trained by {answer.approved_by} · {moment(answer.approved_ts).format('MMM D, YYYY')}
          {answer.copied_from ? ` · copied from the ${CHANNEL_LABELS[other]}` : ''}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function SavedAnswers({ answers, channel, canWrite, canShare, showChannels, handlers, productOptions, api }) {
  const [query, setQuery] = useState('');
  const [removing, setRemoving] = useState(null);
  const [adding, setAdding] = useState(false);
  const other = otherChannel(channel);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return answers;
    return answers.filter((a) => `${a.question} ${a.answer}`.toLowerCase().includes(q));
  }, [answers, query]);

  const removingShared = !!removing?.channels.includes(other);

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search trained answers"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ width: { xs: '100%', sm: 360 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        {canWrite && !adding && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAdding(true)}>
            Add a trained answer
          </Button>
        )}
      </Stack>
      {adding && (
        <NewAnswerCard
          channel={channel}
          canShare={canShare}
          productOptions={productOptions}
          api={api}
          onCreate={handlers.onCreate}
          onCancel={() => setAdding(false)}
        />
      )}
      {shown.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {answers.length
            ? 'No trained answers match your search.'
            : 'Answers you train the chatbot with will appear here. You can also add your own.'}
        </Typography>
      ) : shown.map((a) => (
        <AnswerCard
          key={a.id}
          answer={a}
          channel={channel}
          canWrite={canWrite}
          canShare={canShare}
          showChannels={showChannels}
          handlers={{ ...handlers, onRemove: setRemoving }}
          productOptions={productOptions}
          api={api}
        />
      ))}
      <ConfirmationDialog
        open={!!removing}
        title={removingShared ? `Remove from the ${CHANNEL_LABELS[channel]}?` : 'Delete this trained answer?'}
        okButtonText={removingShared ? 'Remove' : 'Delete'}
        onCancel={() => setRemoving(null)}
        onConfirm={async () => { const a = removing; setRemoving(null); await handlers.onRemove(a); }}
      >
        <Typography variant="body2">
          {removingShared
            ? `The ${CHANNEL_LABELS[channel]} will stop using “${removing?.question}”. The ${CHANNEL_LABELS[other]} keeps it.`
            : `The chatbot will stop using “${removing?.question}”. ${CHANNEL_COPY[channel].removeNote}`}
        </Typography>
      </ConfirmationDialog>
    </Box>
  );
}
